import React, { useState, useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { FileData } from "../network/api/FileData";

const getPreviewSrc = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
        return URL.createObjectURL(file);
    }
    if (ext === 'pdf') return '/assets/icons/file-icons/pdf-icon.png';
    if (['doc', 'docx'].includes(ext)) return '/assets/icons/file-icons/docx-icon.png';
    if (['zip', 'rar', '7z'].includes(ext)) return '/assets/icons/file-icons/zip-icon.png';
    if (ext === 'mp4') return '/assets/icons/file-icons/video-icon.png';
    return '/assets/icons/file-icons/file-icon-2.png';
};

export default function FileUpload({ mode = "image", owner = null, setFileData =null, description= null }) {
    const [files, setFiles] = useState([]);

    const allowedImageTypes = useMemo(() => ['jpg', 'jpeg', 'png', 'gif', 'webp'], []);
    const allowedFileTypes = useMemo(() => [
        'jpg', 'jpeg', 'png', 'gif', 'webp',
        'pdf', 'doc', 'docx', 'zip', 'rar', '7z', 'mp4'
    ], []);

    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB

    const onDrop = useCallback(
        async (acceptedFiles) => {
            const updatedFiles = acceptedFiles.map(file => {
                const ext = file.name.split('.').pop().toLowerCase();
                const allowedTypes = mode === 'image' ? allowedImageTypes : allowedFileTypes;
                if (!allowedTypes.includes(ext)) {
                    return {
                        file,
                        preview: getPreviewSrc(file),
                        status: 'error',
                        progress: 0,
                        errorMessage: 'File type not allowed'
                    };
                }
                if (file.size > MAX_FILE_SIZE) {
                    return {
                        file,
                        preview: getPreviewSrc(file),
                        status: 'error',
                        progress: 0,
                        errorMessage: 'File size exceeds limit (20 MB)'
                    };
                }
                return {
                    file,
                    preview: getPreviewSrc(file),
                    status: 'loading',
                    progress: 0
                };
            });

            const cleanedFiles = updatedFiles.filter(f => f !== null);
            setFiles(prev => [...prev, ...cleanedFiles]);

            cleanedFiles.forEach(async (fileObj, index) => {
                if (fileObj.status === 'error') return;

                const form = new FormData();
                form.append('file', fileObj.file);
                form.append('description', description||'File upload');
                if (owner) form.append('owner', owner);

                try {
                   
                    const res = await FileData({ method: 'POST', formData: form });
                   
                    if(res.status === 'success') {
                        setFiles(prev =>
                            prev.map((f, i) =>
                                i === prev.length - cleanedFiles.length + index
                                    ? {
                                        ...f,
                                        status: res.status === 'success' ? 'success' : 'error',
                                        errorMessage: res.status !== 'success' ? res.message : '',
                                        progress: 100
                                    }
                                    : f
                            )
                        );

                        setFileData(res.data);
                    }else{
                        setFiles(prev =>
                            prev.map((f, i) =>
                                i === prev.length - cleanedFiles.length + index
                                    ? { ...f, status: 'error', errorMessage: 'Upload failed', progress: 0 }
                                    : f
                            )
                        );
                    }
                } catch (error) {
                    setFiles(prev =>
                        prev.map((f, i) =>
                            i === prev.length - cleanedFiles.length + index
                                ? { ...f, status: 'error', errorMessage: 'Upload failed', progress: 0 }
                                : f
                        )
                    );
                }
            });
        },
        [mode, MAX_FILE_SIZE, allowedImageTypes, allowedFileTypes, description, owner, setFileData]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    return (
        <div className="compose-content">

            <div className={`dropzone dz-clickable ${files.length > 0 ? "dz-started" : ""}`} {...getRootProps()}>
                <input type="file" name="file" accept={mode === 'image' ? "image/*" : "*"} {...getInputProps()} />
                <div className="dz-default dz-message ">
                    <p className="dz-button fw-semibold" >{isDragActive ? "Drop the files here ...<" : "Drag & Drop files here or click to upload"}</p>
                </div>

                {files.map((fileObj, index) => (
                    <div
                        key={index}
                        className={`dz-preview   ${fileObj.status === "loading" ? "dz-processing" : "dz-complete"} ${fileObj.status === "success" ? "dz-success" : ""} ${fileObj.status === "error" ? "dz-error" : ""} rounded-5`}>

                        <div className="dz-image" style={{ borderRadius: "0.5rem" }}>
                            <img src={fileObj.preview} className="dz-image" alt={fileObj.file.name}
                                onError={(e) => e.target.src = '/assets/icons/file-icons/file-icon.png'}
                            />
                        </div>

                        <div className="dz-details">
                            <div className="dz-size">
                                <span><strong>{(fileObj.file.size / 1024).toFixed(1)}</strong> KB</span>
                            </div>
                            <div className="dz-filename">
                                <span>{fileObj.file.name}</span>
                            </div>
                        </div>

                        <div className="dz-progress">
                            <span className="dz-upload" style={{ width: `${fileObj.progress}%` }}></span>
                        </div>

                        <div className="dz-error-message">
                            <span>{fileObj.errorMessage || 'Request timedout after 30000 seconds'}</span>
                        </div>

                        <div className="dz-success-mark">
                            <svg width="54px" height="54px" viewBox="0 0 54 54" xmlns="http://www.w3.org/2000/svg">
                                <title>Check</title>
                                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                    <path
                                        d="M23.5,31.8431458 L17.5852419,25.9283877 C16.0248253,24.3679711 13.4910294,24.366835 11.9289322,25.9289322 C10.3700136,27.4878508 10.3665912,30.0234455 11.9283877,31.5852419 L20.4147581,40.0716123 C20.5133999,40.1702541 20.6159315,40.2626649 20.7218615,40.3488435 C22.2835669,41.8725651 24.794234,41.8626202 26.3461564,40.3106978 L43.3106978,23.3461564 C44.8771021,21.7797521 44.8758057,19.2483887 43.3137085,17.6862915 C41.7547899,16.1273729 39.2176035,16.1255422 37.6538436,17.6893022 L23.5,31.8431458 Z M27,53 C41.3594035,53 53,41.3594035 53,27 C53,12.6405965 41.3594035,1 27,1 C12.6405965,1 1,12.6405965 1,27 C1,41.3594035 12.6405965,53 27,53 Z"
                                        strokeOpacity="0.198794158"
                                        stroke="#747474"
                                        fillOpacity="0.816519475"
                                        fill="#FFFFFF"
                                    ></path>
                                </g>
                            </svg>
                        </div>
                        <div className="dz-error-mark">
                            <svg width="54px" height="54px" viewBox="0 0 54 54" xmlns="http://www.w3.org/2000/svg">
                                <title>Error</title>
                                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                    <g stroke="#747474" strokeOpacity="0.198794158" fill="#FFFFFF" fillOpacity="0.816519475">
                                        <path d="M32.6568542,29 L38.3106978,23.3461564 C39.8771021,21.7797521 39.8758057,19.2483887 38.3137085,17.6862915 C36.7547899,16.1273729 34.2176035,16.1255422 32.6538436,17.6893022 L27,23.3431458 L21.3461564,17.6893022 C19.7823965,16.1255422 17.2452101,16.1273729 15.6862915,17.6862915 C14.1241943,19.2483887 14.1228979,21.7797521 15.6893022,23.3461564 L21.3431458,29 L15.6893022,34.6538436 C14.1228979,36.2202479 14.1241943,38.7516113 15.6862915,40.3137085 C17.2452101,41.8726271 19.7823965,41.8744578 21.3461564,40.3106978 L27,34.6568542 L32.6538436,40.3106978 C34.2176035,41.8744578 36.7547899,41.8726271 38.3137085,40.3137085 C39.8758057,38.7516113 39.8771021,36.2202479 38.3106978,34.6538436 L32.6568542,29 Z M27,53 C41.3594035,53 53,41.3594035 53,27 C53,12.6405965 41.3594035,1 27,1 C12.6405965,1 1,12.6405965 1,27 C1,41.3594035 12.6405965,53 27,53 Z"></path>
                                    </g>
                                </g>
                            </svg>
                        </div>


                    </div>
                ))}

            </div>

        </div>
    );
}